import { CostCenterFieldStatus, CostCenterOption, CostCenterType } from '@lib/state';
import { CostCenterFieldConfiguration } from '@lib/state';
import { FormCheckbox, FormTextField, FormSelect } from '../../forms';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { MenuItem } from '@material-ui/core';

interface Props {
  name: string;
  corporateAccountId?: string;
  config: CostCenterFieldConfiguration;
  costCenterOptions?: Array<CostCenterOption>;
  disabled?: boolean;
  isDelegateOrEmployee: boolean;
  required?: boolean;
}

const CostCenterFieldRequired: React.FC<Props> = ({
  name,
  config,
  costCenterOptions,
  isDelegateOrEmployee,
  disabled,
  required,
  corporateAccountId, // extracted to avoid forwarding to DOM
  ...props
}) => {
  const [isRequiredOverridden, setIsRequiredOverridden] = useState<boolean>(false);

  const shouldDisplayRequiredCheckbox: boolean = useMemo(() => {
    return isDelegateOrEmployee && config.status === CostCenterFieldStatus.Required;
  }, [isDelegateOrEmployee, config.status]);

  const { setValue, watch, register, clearError } = useFormContext();
  const selectedValue = watch(`${name}_select`);
  const otherValue = watch(`${name}_other`);
  const activeValue = watch(`${name}`);

  const shouldShowOtherTextField: boolean = useMemo(() => {
    return config.type === CostCenterType.Select && selectedValue === 'other';
  }, [config.type, selectedValue]);

  // Update the main form field when select value changes
  useEffect(() => {
    if (config.type === CostCenterType.Select) {
      if (selectedValue === 'other') {
        // For "other" option, use the text input value
        setValue(name, otherValue || '', true);
      } else if (selectedValue && selectedValue === 'None') {
        // For regular options, use the selected value
        setValue(name, '', true);
      } else if (selectedValue && selectedValue !== '') {
        // For regular options, use the selected value
        setValue(name, selectedValue, true);
      } else {
        // For empty/none selection, clear the value
        setValue(name, '', true);
      }
    }
  }, [selectedValue, otherValue, name, setValue, config.type]);

  // Clear the "other" field when a different option is selected
  useEffect(() => {
    if (config.type === CostCenterType.Select && selectedValue !== 'other') {
      setValue(`${name}_other`, '', true);
    }
  }, [selectedValue, name, setValue, config.type]);

  const requiredInfoMissingOnChange = useCallback(() => {
    setValue(name, '');
    setIsRequiredOverridden(!isRequiredOverridden);
    if (!isRequiredOverridden) clearError(name);
    if (
      !isRequiredOverridden &&
      config.type === CostCenterType.Select &&
      selectedValue !== 'other'
    ) {
      setValue(`${name}_select`, 'None');
      setValue(`${name}_other`, '');
    }

    if (
      isRequiredOverridden &&
      config.type === CostCenterType.Select &&
      selectedValue !== 'other'
    ) {
      setValue(`${name}_select`, '');
      setValue(`${name}_other`, '');
    }
  }, [config.type, name, selectedValue, setValue, isRequiredOverridden]);

  // Register field with conditional validation
  useEffect(() => {
    register(name, {
      required:
        !isRequiredOverridden &&
        !disabled &&
        (config.status === CostCenterFieldStatus.Required || required)
          ? 'This field is required'
          : false,
    });
  }, [isRequiredOverridden, disabled, required, register, name]);

  const validateField = useCallback(
    (value: string) => {
      if (isRequiredOverridden) return true; // Skip validation if overridden

      if (config.status === CostCenterFieldStatus.Required || required) {
        return value && value.trim() !== '' ? true : 'This field is required';
      }

      return true;
    },
    [isRequiredOverridden, config.status, required]
  );

  return (
    <>
      {config.type === CostCenterType.Select ? (
        <>
          <input type="hidden" name={name} ref={register} defaultValue={activeValue ?? ''} />
          <FormSelect
            key={`${name}_select_override_${isRequiredOverridden}`}
            {...props}
            name={`${name}_select`}
            label={config.label}
            disabled={isRequiredOverridden || disabled}
            fullWidth
            defaultValue={''}
            validationOptions={{
              required:
                !isRequiredOverridden &&
                !disabled &&
                (config.status === CostCenterFieldStatus.Required || required),
            }}
            data-testid={`${config.key}Select`}
          >
            <MenuItem key="None" value="">
              None
            </MenuItem>
            {costCenterOptions
              ?.filter((x, _) => x)
              .map(x => (
                <MenuItem key={x.value} value={x.value ?? ''}>
                  {x.label}
                </MenuItem>
              ))}
            <MenuItem key="other" value="other">
              Other
            </MenuItem>
          </FormSelect>
          {shouldShowOtherTextField && (
            <FormTextField
              key={`${name}_other_override_${isRequiredOverridden}`}
              {...props}
              name={`${name}_other`}
              label="Please specify"
              type="text"
              fullWidth
              disabled={isRequiredOverridden || disabled}
              validationOptions={{
                validate: validateField,
              }}
              data-testid={`${config.key}OtherInput`}
            />
          )}
        </>
      ) : (
        <FormTextField
          key={`${name}_input_override_${isRequiredOverridden}`}
          {...props}
          name={name}
          label={
            config.status === CostCenterFieldStatus.Required
              ? config.label
              : `${config.label} (Optional)`
          }
          type="text"
          fullWidth
          disabled={isRequiredOverridden || disabled}
          validationOptions={{
            required:
              !isRequiredOverridden &&
              !disabled &&
              (config.status === CostCenterFieldStatus.Required || required),
          }}
          data-testid={`${config.key}Input`}
        />
      )}
      {shouldDisplayRequiredCheckbox && (
        <FormCheckbox
          name={`${name}_Override_Required`}
          label="Required billing info missing"
          {...({ checked: isRequiredOverridden, onChange: requiredInfoMissingOnChange } as any)}
        />
      )}
    </>
  );
};

export default CostCenterFieldRequired;
